import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Phone, Mail, Heart, Sparkles, Gift, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useSEO } from '../hooks/useSEO';

export const NewToShapewearPage = () => {
  useSEO({
    title: 'New to Shapewear? Start Here | CurveeForYou',
    description: "Never worn a faja or shapewear before? Mel and Mike walk you through what it feels like, what to wear under what, and how to choose the right style for your body — no overwhelm. Plus the easiest first step for newcomers.",
    canonical: 'https://curveeforyou.com/new-to-shapewear'
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/40 to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" data-testid="nts-nav-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_item-landing/artifacts/feqbyn90_Logo%20curveeforyou.png"
              alt="CurveeForYou"
              className="h-20 md:h-24 w-auto"
            />
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/full-body-faja" className="text-gray-700 hover:text-[#7d1f3d] font-medium">Full Body Faja</Link>
            <Link to="/open-bust-faja" className="text-gray-700 hover:text-[#7d1f3d] font-medium">Open-Bust Faja</Link>
            <Link to="/about" className="text-gray-700 hover:text-[#7d1f3d] font-medium">About</Link>
            <Link to="/" data-testid="nts-nav-bundle-cta">
              <Button className="bg-[#7d1f3d] hover:bg-[#5d1729] text-white px-5">
                <ShoppingBag className="w-4 h-4 mr-2" /> Get the Bundle
              </Button>
            </Link>
          </div>
          <Link to="/" className="md:hidden">
            <Button variant="outline" size="sm" className="border-[#7d1f3d] text-[#7d1f3d]">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </header>

      <article className="container mx-auto px-5 py-10 md:py-16 max-w-3xl">

        {/* Hero */}
        <div className="text-center mb-14">
          <Badge className="bg-[#d4af37] text-[#5d1729] font-bold px-4 py-1.5 mb-5 uppercase tracking-wider text-xs">
            For First-Time Faja Wearers
          </Badge>
          <h1 className="text-3xl md:text-5xl font-bold text-[#7d1f3d] leading-[1.15] mb-6" data-testid="nts-h1">
            New to Shapewear? You&apos;re Closer to That Feeling Than You Think.
          </h1>
          <div className="space-y-4 text-base md:text-lg text-gray-700 leading-relaxed text-left">
            <p>
              You know the one. That moment you zip up a dress and everything just sits right. Your waist looks like your waist. Your chest is lifted. Your thighs are smooth. You look in the mirror and for once, you don&apos;t adjust anything — you just go.
            </p>
            <p>
              That feeling is not reserved for a certain body type. It is not luck. It is not a good angle or the right lighting.
            </p>
            <p className="text-[#7d1f3d] font-bold text-lg md:text-xl">
              It is a faja. And it is waiting for you.
            </p>
          </div>
        </div>

        {/* Section: We know how you've been feeling */}
        <section className="mb-14 bg-white border border-rose-100 rounded-2xl shadow-sm p-7 md:p-10">
          <div className="flex items-center gap-2 mb-4">
            <Heart className="w-5 h-5 text-[#7d1f3d]" />
            <h2 className="text-2xl md:text-3xl font-bold text-[#7d1f3d]">We Know What You&apos;ve Been Feeling.</h2>
          </div>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              You&apos;ve stood in front of a mirror in an outfit you love and felt let down by what you saw. You&apos;ve skipped events, changed your plans, or settled for something that didn&apos;t make you feel <em>her</em> — the version of you that walks in and owns the room.
            </p>
            <p>
              Maybe you&apos;ve tried shapewear before and it rolled down, dug in, or flattened the wrong things. Maybe you&apos;ve never tried it and you don&apos;t know where to start.
            </p>
            <p>
              Either way, Mel and Mike built <strong>CurveeForYou</strong> for exactly this moment. Not to change who you are — to finally let you feel like yourself in every single outfit.
            </p>
          </div>
        </section>

        {/* Section: Everything you need to know */}
        <section className="mb-14">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="w-5 h-5 text-[#d4af37]" />
            <h2 className="text-2xl md:text-3xl font-bold text-[#7d1f3d]">
              Everything You Need to Know <span className="text-gray-500 italic font-normal">(Without the Overwhelm)</span>
            </h2>
          </div>

          <div className="space-y-6">
            {[
              {
                q: 'What will it actually feel like?',
                a: 'Like a hug that holds you in all the right places. Supportive, not suffocating. You will breathe. You will sit. You will move through your day — and you will feel put together in a way that no amount of tugging and adjusting ever gave you.'
              },
              {
                q: 'Will it be uncomfortable?',
                a: "Good shapewear should never hurt. If it does, it's the wrong size. Our size guide is built to get you right the first time — and if you're not sure, Mel and Mike's team will walk you through it personally. That's a promise."
              },
              {
                q: 'Should I size up?',
                a: 'If you want that soft, seamless everyday feeling — yes. If you want to feel genuinely snatched for a big night — go by your measurements. Either way, you will feel the difference the moment you put it on.'
              },
              {
                q: "What's the difference between smoothing and sculpting?",
                a: "Smoothing is the quiet confidence of everyday wear — light, invisible, effortless. Sculpting is the feeling you get when you put on a fitted dress and your silhouette looks like it was made for it. Both have their moment. Both are yours."
              },
              {
                q: 'Can I wear it every day?',
                a: "Yes. Our lighter styles were designed to feel like a second skin — breathable, seamless, and comfortable enough that you forget you're wearing them. The kind of thing you reach for without thinking."
              },
              {
                q: 'What can I wear it under?',
                a: 'Everything you already love. Dresses, skirts, jumpsuits, workwear, fitted basics. The right style depends on your outfit and how you want to feel in it.'
              },
              {
                q: 'How do I look after it?',
                a: 'Gentle wash, lay flat to dry. Treat it the way it treats you — with care — and it will last.'
              }
            ].map((item) => (
              <div key={item.q} className="border-l-4 border-[#d4af37] pl-5 py-1">
                <h3 className="text-lg md:text-xl font-bold text-[#7d1f3d] mb-2">{item.q}</h3>
                <p className="text-gray-700 leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How do I choose? — picker block */}
        <section className="mb-14 bg-gradient-to-br from-[#7d1f3d] to-[#5d1729] text-white rounded-2xl shadow-xl p-7 md:p-10">
          <h2 className="text-2xl md:text-3xl font-bold mb-2">How Do I Choose?</h2>
          <p className="text-rose-100 mb-7">Start with the feeling you&apos;re chasing.</p>

          <div className="space-y-3">
            {[
              {
                feeling: '"I want my tummy smooth under this dress"',
                pick: 'High-waist faja brief',
                to: '/open-bust-faja',
                cta: 'See it'
              },
              {
                feeling: '"I want to feel completely snatched — bust to thigh"',
                pick: 'Full Body Faja Colombiana (built-in bra included)',
                to: '/full-body-faja',
                cta: 'See it'
              },
              {
                feeling: '"I\u2019m wearing strapless and I need support"',
                pick: 'Open-Bust Tummy Control Faja',
                to: '/open-bust-faja',
                cta: 'See it'
              },
              {
                feeling: '"I just want something soft for every day"',
                pick: 'Seamless Boyshorts',
                to: '/',
                cta: 'Get with the bundle'
              },
              {
                feeling: '"I want the full transformation in one order"',
                pick: 'The Snatched Duo Bundle — both fajas, plus boyshorts free',
                to: '/',
                cta: 'Get the bundle',
                highlight: true
              }
            ].map((row) => (
              <Link
                key={row.pick}
                to={row.to}
                data-testid={`nts-picker-${row.to.replace(/\//g, '-')}`}
                className={`block rounded-xl p-4 md:p-5 transition-all border ${row.highlight
                  ? 'bg-[#d4af37] text-[#5d1729] border-[#d4af37] hover:bg-[#c49d2a]'
                  : 'bg-white/5 border-white/20 hover:bg-white/10'
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm md:text-base italic ${row.highlight ? 'text-[#5d1729]/80' : 'text-rose-100'}`}>
                      {row.feeling}
                    </p>
                    <p className={`font-bold text-base md:text-lg mt-1 ${row.highlight ? 'text-[#5d1729]' : 'text-white'}`}>
                      {row.pick}
                    </p>
                  </div>
                  <div className={`text-xs md:text-sm font-bold uppercase tracking-wider whitespace-nowrap ${row.highlight ? 'text-[#5d1729]' : 'text-[#d4af37]'}`}>
                    {row.cta} →
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Never worn shapewear before */}
        <section className="mb-14 bg-white border border-rose-100 rounded-2xl shadow-sm p-7 md:p-10">
          <Badge className="bg-rose-100 text-[#7d1f3d] font-bold mb-4">Start Here</Badge>
          <h2 className="text-2xl md:text-3xl font-bold text-[#7d1f3d] mb-5">Never Worn Shapewear Before?</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              The easiest first step is our <strong>seamless boyshorts</strong>. Soft, smooth, zero learning curve. Once you feel what it does for your silhouette, you will understand why women who find their faja never go back.
            </p>
            <p>
              And when you&apos;re ready for the full feeling — the lifted bust, the cinched waist, the smooth thighs, the walk-in-and-own-it confidence — <strong className="text-[#7d1f3d]">The Snatched Duo Bundle</strong> is where it starts.
            </p>
            <div className="grid grid-cols-3 gap-3 pt-3">
              <div className="text-center bg-rose-50/60 rounded-lg p-3">
                <CheckCircle2 className="w-5 h-5 text-[#7d1f3d] mx-auto mb-1" />
                <p className="text-xs font-semibold">Two fajas</p>
              </div>
              <div className="text-center bg-rose-50/60 rounded-lg p-3">
                <Gift className="w-5 h-5 text-[#d4af37] mx-auto mb-1" />
                <p className="text-xs font-semibold">Free boyshorts</p>
              </div>
              <div className="text-center bg-rose-50/60 rounded-lg p-3">
                <Sparkles className="w-5 h-5 text-[#7d1f3d] mx-auto mb-1" />
                <p className="text-xs font-semibold">Bust to thigh</p>
              </div>
            </div>
            <p className="text-center text-lg font-bold text-[#7d1f3d] pt-2">
              Two fajas. Free boyshorts. One order. Bust to thigh.
            </p>
            <div className="pt-3 text-center">
              <Link to="/" data-testid="nts-bundle-cta">
                <Button size="lg" className="bg-[#7d1f3d] hover:bg-[#5d1729] text-white font-bold px-7 py-6 shadow-lg">
                  <ShoppingBag className="w-5 h-5 mr-2" />
                  Get the Snatched Duo Bundle
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Mel and Mike contact */}
        <section className="mb-6 bg-gradient-to-br from-rose-50 to-white border-2 border-[#d4af37]/40 rounded-2xl shadow-sm p-7 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-[#7d1f3d] mb-3">Mel and Mike Are Here. Personally.</h2>
          <p className="text-gray-700 leading-relaxed max-w-xl mx-auto mb-6">
            No chatbots. No automated replies. If you have a question — about sizing, about which style is right for your body, about anything — reach out. They built this brand to help you feel incredible, and that does not stop at checkout.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="tel:+18002985173"
              data-testid="nts-call"
              className="flex items-center gap-2 bg-[#7d1f3d] hover:bg-[#5d1729] text-white px-5 py-3 rounded-lg font-semibold transition-colors"
            >
              <Phone className="w-4 h-4" /> 1-800-298-5173
            </a>
            <a
              href="mailto:info@curveeforyou.com"
              data-testid="nts-email"
              className="flex items-center gap-2 bg-white hover:bg-rose-50 text-[#7d1f3d] border-2 border-[#7d1f3d] px-5 py-3 rounded-lg font-semibold transition-colors"
            >
              <Mail className="w-4 h-4" /> info@curveeforyou.com
            </a>
          </div>
          <p className="text-sm text-gray-500 italic mt-6">
            You deserve to feel snatched. We&apos;re here until you do.
          </p>
        </section>
      </article>
    </div>
  );
};

export default NewToShapewearPage;
